import { useGoogleMap } from "#/hook/useGoogleMap";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClickAway } from "react-use";
import { MARKER_ICON_SIZE } from "./map/mapConstants";
import { getMarkerIconSvg } from "./map/mapIcons";
import type { MapMarker, ResolvedMapMarker } from "./map/types";
import { useMarkerClusterer, type MarkerSelection } from "./map/useMarkerClusterer";

export interface InventoryMapProps {
  markers: MapMarker[];
  zoom: number;
  additionalComponent?: React.ReactNode;
  options?: google.maps.MapOptions;
  /**
   * Groups the markers into clusters that split apart as the user zooms in. Off
   * by default, so single-pin maps keep rendering plain pins.
   */
  enableClustering?: boolean;
}

const DEFAULT_CENTER = { lat: 0, lng: 0 };
/** Gap kept between an open popup and the map box edges after auto-pan, in px. */
const POPUP_EDGE_PADDING = 12;
const WORLD_ZOOM = 2;
/** Shared instance so an omitted `options` prop never looks like a change. */
const EMPTY_OPTIONS: google.maps.MapOptions = {};

/** Coordinates arrive as numbers or strings, and `0` means "never geocoded". */
const resolveMarkers = (markers: MapMarker[]): ResolvedMapMarker[] =>
  markers
    .map((marker) => ({
      ...marker,
      latitude:
        typeof marker.latitude === "string" ? parseFloat(marker.latitude) : marker.latitude,
      longitude:
        typeof marker.longitude === "string" ? parseFloat(marker.longitude) : marker.longitude,
    }))
    .filter(
      (marker): marker is ResolvedMapMarker =>
        typeof marker.latitude === "number" &&
        !isNaN(marker.latitude) &&
        marker.latitude !== 0 &&
        typeof marker.longitude === "number" &&
        !isNaN(marker.longitude) &&
        marker.longitude !== 0
    );

/**
 * Cheap fingerprint of the rendered marker set. A rolling hash rather than a
 * joined string, because a client can have tens of thousands of items and this
 * runs on every render.
 */
const getMarkersSignature = (markers: ResolvedMapMarker[]): string => {
  let hash = 0;

  for (const marker of markers) {
    hash = (Math.imul(hash, 31) + Math.round(marker.latitude * 1e6)) | 0;
    hash = (Math.imul(hash, 31) + Math.round(marker.longitude * 1e6)) | 0;
    for (let index = 0; index < marker.color.length; index += 1) {
      hash = (Math.imul(hash, 31) + marker.color.charCodeAt(index)) | 0;
    }
  }

  return `${markers.length}:${hash}`;
};

const InventoryMap: React.FC<InventoryMapProps> = ({
  markers,
  zoom,
  options,
  additionalComponent,
  enableClustering = false,
}) => {
  const { isLoaded } = useGoogleMap();

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selection, setSelection] = useState<MarkerSelection | null>(null);
  const infoWindowRef = useRef<HTMLDivElement | null>(null);

  const validMarkers = useMemo(() => resolveMarkers(markers), [markers]);

  /**
   * Parents rebuild the `markers` array on every render, so the imperative
   * layers key off this fingerprint instead of the array identity — otherwise
   * every render would tear down and re-create hundreds of map objects.
   */
  const markersSignature = useMemo(() => getMarkersSignature(validMarkers), [validMarkers]);

  useMarkerClusterer({
    map,
    markers: validMarkers,
    signature: markersSignature,
    enabled: enableClustering,
    onSelect: setSelection,
  });

  useClickAway(infoWindowRef, () => {
    setSelection(null);
  });

  // Clicks and drags on the popup must act on the popup (its links), not pan
  // the map or count as a map click underneath it.
  useEffect(() => {
    const element = infoWindowRef.current;
    if (!selection || !element) return;
    google.maps.OverlayView.preventMapHitsAndGesturesFrom(element);
  }, [selection]);

  // The map box clips anything past its edges, so a popup opened near an edge
  // loses its top or side. Once the popup has rendered, measure it against the
  // map box and pan by the smallest distance that brings all of it inside,
  // like Google's own info windows. Centring the pin is not enough: on a short
  // map the card is taller than the space above the centre.
  useEffect(() => {
    if (!selection || !map) return;

    let frame = 0;
    const timeout = setTimeout(() => {
      frame = requestAnimationFrame(() => {
        const popup = infoWindowRef.current;
        if (!popup) return;
        const popupRect = popup.getBoundingClientRect();
        const mapRect = map.getDiv().getBoundingClientRect();
        if (popupRect.width === 0 || popupRect.height === 0) return;

        // panBy moves the viewport, so content shifts the opposite way:
        // negative dy pulls content down (fixes a clipped top), positive dx
        // pulls content left (fixes a clipped right side).
        let dx = 0;
        let dy = 0;
        const overflowTop = mapRect.top + POPUP_EDGE_PADDING - popupRect.top;
        const overflowBottom = popupRect.bottom - (mapRect.bottom - POPUP_EDGE_PADDING);
        if (overflowTop > 0) dy = -overflowTop;
        else if (overflowBottom > 0) dy = overflowBottom;

        const overflowLeft = mapRect.left + POPUP_EDGE_PADDING - popupRect.left;
        const overflowRight = popupRect.right - (mapRect.right - POPUP_EDGE_PADDING);
        if (overflowLeft > 0) dx = -overflowLeft;
        else if (overflowRight > 0) dx = overflowRight;

        if (dx !== 0 || dy !== 0) map.panBy(dx, dy);
      });
    }, 50);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(frame);
    };
  }, [selection, map]);

  // Nothing left to point at once the markers change (e.g. a status filter).
  useEffect(() => {
    setSelection(null);
  }, [markersSignature]);

  const centerLat = validMarkers[0]?.latitude;
  const centerLng = validMarkers[0]?.longitude;

  // Identity must stay stable across renders or the map re-centres, throwing
  // away whatever the user had panned or zoomed to.
  const mapCenter = useMemo(
    () =>
      centerLat !== undefined && centerLng !== undefined
        ? { lat: centerLat, lng: centerLng }
        : DEFAULT_CENTER,
    [centerLat, centerLng]
  );

  const validMarkersRef = useRef(validMarkers);
  validMarkersRef.current = validMarkers;

  const handleLoad = useCallback((loadedMap: google.maps.Map) => {
    setMap(loadedMap);

    const bounds = new google.maps.LatLngBounds();
    for (const marker of validMarkersRef.current) {
      bounds.extend({ lat: marker.latitude, lng: marker.longitude });
    }
    if (!bounds.isEmpty()) {
      loadedMap.fitBounds(bounds);
    }
  }, []);

  const handleUnmount = useCallback(() => setMap(null), []);

  return (
    <div className="relative h-full w-full">
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={validMarkers.length > 0 ? zoom : WORLD_ZOOM}
          onLoad={handleLoad}
          onUnmount={handleUnmount}
          options={options || EMPTY_OPTIONS}
        >
          {!enableClustering &&
            validMarkers.map((marker, index) => (
              <Marker
                key={`${marker.longitude}_${marker.latitude}_${index}`}
                position={{
                  lat: marker.latitude,
                  lng: marker.longitude,
                }}
                icon={{
                  url: getMarkerIconSvg(marker.color),
                  scaledSize: new google.maps.Size(MARKER_ICON_SIZE, MARKER_ICON_SIZE),
                  anchor: new google.maps.Point(MARKER_ICON_SIZE / 2, MARKER_ICON_SIZE),
                }}
                onClick={() =>
                  setSelection({
                    marker,
                    position: { lat: marker.latitude, lng: marker.longitude },
                  })
                }
              />
            ))}

          {selection && (
            <OverlayView
              position={selection.position}
              mapPaneName={OverlayView.FLOAT_PANE}
            >
              <div
                ref={infoWindowRef}
                style={{
                  position: "absolute",
                  // Sit just above the pin head, leaving room for a callout pointer.
                  transform: `translate(-50%, calc(-100% - ${MARKER_ICON_SIZE + 8}px))`,
                  maxWidth: "320px",
                  zIndex: 1000,
                }}
              >
                {selection.marker.infoWindowContent?.()}
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      )}

      {additionalComponent && additionalComponent}
    </div>
  );
};

export default React.memo(InventoryMap);
